import React, { useEffect, useRef, useState } from "react";
import { Input, Button, Tag, Alert, Select, Radio, Tooltip } from "antd";
import { UserOutlined } from "@ant-design/icons";
import styles from "./AIConfigContent.css"; // Updated CSS import
import { Api } from "@/api/api";
import { showConfirm, showDanger, showSuccess } from "@/utils";
import FunctionManagementDrawer from "../../FunctionManagementDrawer";
import { useLoading } from "@/components/global-loading";
import { FormattedMessage, injectIntl } from "react-intl";

const { TextArea } = Input;

const functionColorMap = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FFEEAD",
    "#D4A5A5",
    "#A2836E",
];

const AIConfigContent = ({ setTitle, agentId, onSave, intl }) => {
    const allFunctionsRef = useRef();
    const llmModeTypeMapRef = useRef(new Map());
    const curLLMIdRef = useRef();
    const modalOptionsRef = useRef();
    const [formData, setFormData] = useState({});
    const [templates, setTemplates] = useState([]);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const [models, setModels] = useState([
        { label: intl.formatMessage({ id: "gui.agentManage.vad", defaultMessage: "Voice Activity Detection(VAD)" }), key: "vadModelId", type: "VAD" },
        { label: intl.formatMessage({ id: "gui.agentManage.asr", defaultMessage: "Automatic Speech Recognition(ASR)" }), key: "asrModelId", type: "ASR" },
        { label: intl.formatMessage({ id: "gui.agentManage.llm", defaultMessage: "Large Language Model(LLM)" }), key: "llmModelId", type: "LLM" },
        { label: intl.formatMessage({ id: "gui.agentManage.vllm", defaultMessage: "Vision Large Language Model(VLLM)" }), key: "vllmModelId", type: "VLLM" },
        { label: intl.formatMessage({ id: "gui.agentManage.intent", defaultMessage: "Intent Recognition(Intent)" }), key: "intentModelId", type: "Intent" },
        { label: intl.formatMessage({ id: "gui.agentManage.memory", defaultMessage: "Memory(Memory)" }), key: "memModelId", type: "Memory" },
        { label: intl.formatMessage({ id: "gui.agentManage.tts", defaultMessage: "Text to Speech(TTS)" }), key: "ttsModelId", type: "TTS" },
    ]);

    const [modelOptions, setModelOptions] = useState({});
    const [voiceOptions, setVoiceOptions] = useState([]);
    const [currentFunctions, setCurrentFunctions] = useState([]);
    const { showLoading, hideLoading } = useLoading();

    const intentButtons = [
        { label: "服", color: "#FF6B6B", type: "danger" },
        { label: "中", color: "#4ECDC4", type: "success" },
        { label: "天", color: "#AE8CFF", type: "primary" },
    ];

    useEffect(() => {
        if (formData?.model?.ttsModelId) {
            fetchVoiceOptions(formData.model.ttsModelId);
        }
    }, [formData?.model?.ttsModelId]);

    useEffect(() => {
        if (formData?.modal?.llmModelId) {
            curLLMIdRef.current = formData.modal.llmModelId;
        }
    }, [formData?.modal?.llmModelId]);

    useEffect(() => {
        modalOptionsRef.current = modelOptions;
    }, [modelOptions]);

    useEffect(() => {
        initData();
    }, []);

    const initData = async () => {
        try {
            await fetchAllFunctions();
            fetchAgentConfig();
            fetchTemplates();
        } catch (error) {
            console.log("error: ", error);
        }
    };

    const fetchAllFunctions = () => {
        return new Promise((resolve, reject) => {
            Api.model.getPluginFunctionList(null, ({ data }) => {
                if (data.code === 0) {
                    allFunctionsRef.current = data.data.map((item) => {
                        const meta = JSON.parse(item.fields || "[]");
                        const params = meta.reduce((m, f) => {
                            m[f.key] = f.default;
                            return m;
                        }, {});
                        return { ...item, fieldsMeta: meta, params };
                    });
                    resolve();
                } else {
                    showDanger(data.msg || intl.formatMessage({ id: "gui.agentManage.getPluginListFailed", defaultMessage: "Failed to get plugin list" }));
                    reject();
                }
            });
        });
    };

    const fetchTemplates = () => {
        Api.agent.getAgentTemplate(({ data }) => {
            if (data.code === 0) {
                setTemplates(data.data);
            } else {
                showDanger(data.msg || intl.formatMessage({ id: "gui.agentManage.getTemplateListFailed", defaultMessage: "Failed to get template list" }));
            }
        });
    };

    const fetchModelOptions = () => {
        models.forEach((model) => {
            if (model.type != "LLM") {
                Api.model.getModelNames(model.type, "", ({ data }) => {
                    if (data.code === 0) {
                        setModelOptions((old) => {
                            const newOptions = {
                                ...old,
                                [model.type]: data.data.map((item) => ({
                                    value: item.id,
                                    label: item.modelName,
                                    isHidden: false,
                                })),
                            };

                            // 如果是意图识别选项，需要根据当前LLM类型更新可见性
                            if (model.type === "Intent") {
                                modalOptionsRef.current = newOptions;
                                updateIntentOptionsVisibility();
                            }
                            return newOptions;
                        });
                    } else {
                        showDanger(data.msg || intl.formatMessage({ id: "gui.agentManage.getModelListFailed", defaultMessage: "Failed to get model list" }));
                    }
                });
            } else {
                Api.model.getLlmModelCodeList("", ({ data }) => {
                    if (data.code === 0) {
                        let LLMdata = [];
                        data.data.forEach((item) => {
                            LLMdata.push({
                                value: item.id,
                                label: item.modelName,
                                isHidden: false,
                            });
                            llmModeTypeMapRef.current.set(item.id, item.type);
                        });
                        setModelOptions((old) => {
                            return {
                                ...old,
                                [model.type]: LLMdata,
                            };
                        });
                    } else {
                        showDanger(data.msg || intl.formatMessage({ id: "gui.agentManage.getLlmModelListFailed", defaultMessage: "Failed to get LLM model list" }));
                    }
                });
            }
        });
    };

    const applyTemplateData = (templateData) => {
        const formDataNew = {
            ...formData,
            agentName: templateData.agentName || formData.agentName,
            ttsVoiceId: templateData.ttsVoiceId || formData.ttsVoiceId,
            chatHistoryConf:
                templateData.chatHistoryConf || formData.chatHistoryConf,
            systemPrompt: templateData.systemPrompt || formData.systemPrompt,
            summaryMemory: templateData.summaryMemory || formData.summaryMemory,
            langCode: templateData.langCode || formData.langCode,
            model: {
                ttsModelId:
                    templateData.ttsModelId || formData.model.ttsModelId,
                vadModelId:
                    templateData.vadModelId || formData.model.vadModelId,
                asrModelId:
                    templateData.asrModelId || formData.model.asrModelId,
                llmModelId:
                    templateData.llmModelId || formData.model.llmModelId,
                vllmModelId:
                    templateData.vllmModelId || formData.model.vllmModelId,
                memModelId:
                    templateData.memModelId || formData.model.memModelId,
                intentModelId:
                    templateData.intentModelId || formData.model.intentModelId,
            },
        };
        setTitle(formDataNew.agentName);

        setFormData(formDataNew);
        showSuccess(intl.formatMessage(
            { id: "gui.agentManage.templateApplied", defaultMessage: "「{name}」template applied" },
            { name: formDataNew.agentName }
        ));
    };

    const fetchAgentConfig = () => {
        Api.agent.getWakeup_words(agentId, ({ data }) => {
            if (data.code === 0) {
                // console.log("data");
                // console.log(data);
            } else {
                this.$message.error(data.msg || intl.formatMessage({ id: "gui.agentManage.getConfigFailed", defaultMessage: "Failed to get configuration" }));
            }
        });
        Api.agent.getDeviceConfig(agentId, ({ data }) => {
            if (data.code === 0) {
                curLLMIdRef.current = data.data.llmModelId;
                const formDataNew = {
                    ...data.data,
                    model: {
                        ttsModelId: data.data.ttsModelId,
                        vadModelId: data.data.vadModelId,
                        asrModelId: data.data.asrModelId,
                        llmModelId: data.data.llmModelId,
                        vllmModelId: data.data.vllmModelId,
                        memModelId: data.data.memModelId,
                        intentModelId: data.data.intentModelId,
                    },
                };
                setTitle(formDataNew?.agentName);
                setFormData(formDataNew);
                // 后端只给了最小映射：[{ id, agentId, pluginId }, ...]
                const savedMappings = data.data.functions || [];

                // 合并：按照 pluginId（id 字段）把全量元数据信息补齐
                const tempCurrentFunctions = savedMappings.map((mapping) => {
                    const meta = allFunctionsRef.current.find(
                        (f) => f.id === mapping.pluginId
                    );
                    if (!meta) {
                        // 插件定义没找到，退化处理
                        return {
                            id: mapping.pluginId,
                            name: mapping.pluginId,
                            params: {},
                        };
                    }
                    return {
                        id: mapping.pluginId,
                        name: meta.name,
                        // 后端如果还有 paramInfo 字段就用 mapping.paramInfo，否则用 meta.params 默认值
                        params: mapping.paramInfo || { ...meta.params },
                        fieldsMeta: meta.fieldsMeta, // 保留以便对话框渲染 tooltip
                    };
                });
                setCurrentFunctions(tempCurrentFunctions);
                // 备份原始，以备取消时恢复
                // const originalFunctions = JSON.parse(
                //     JSON.stringify(currentFunctions)
                // );

                // 确保意图识别选项的可见性正确
                fetchModelOptions();
            } else {
                this.$message.error(data.msg || intl.formatMessage({ id: "gui.agentManage.getConfigFailed", defaultMessage: "Failed to get configuration" }));
            }
        });
    };

    const updateIntentOptionsVisibility = () => {
        // 根据当前选择的LLM类型更新意图识别选项的可见性
        const currentLlmId = curLLMIdRef.current;
        if (!currentLlmId || !modalOptionsRef.current["Intent"]) return;

        const llmType = llmModeTypeMapRef.current.get(currentLlmId);
        if (!llmType) return;

        setModelOptions((prevOptions) => {
            const updatedOptions = { ...prevOptions };

            updatedOptions["Intent"].forEach((item) => {
                if (item.value === "Intent_function_call") {
                    // 如果llmType是openai或ollama，允许选择function_call
                    // 否则隐藏function_call选项
                    if (llmType === "openai" || llmType === "ollama") {
                        item.isHidden = false;
                    } else {
                        item.isHidden = true;
                    }
                } else {
                    // 其他意图识别选项始终可见
                    item.isHidden = false;
                }
            });

            // 如果当前选择的意图识别是function_call，但LLM类型不支持，则设置为可选的第一项
            if (
                formData?.model?.intentModelId === "Intent_function_call" &&
                llmType !== "openai" &&
                llmType !== "ollama"
            ) {
                // 找到第一个可见的选项
                const firstVisibleOption = updatedOptions["Intent"].find(
                    (item) => !item.isHidden
                );
                if (firstVisibleOption) {
                    setFormData((prevData) => ({
                        ...prevData,
                        model: {
                            ...prevData.model,
                            intentModelId: firstVisibleOption.value,
                        },
                    }));
                } else {
                    // 如果没有可见选项，设置为Intent_nointent
                    setFormData((prevData) => ({
                        ...prevData,
                        model: {
                            ...prevData.model,
                            intentModelId: "Intent_nointent",
                        },
                    }));
                }
            }

            return updatedOptions;
        });
    };

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handlePresetSelect = (preset) => {
        setFormData((prev) => ({
            ...prev,
            selectedPreset: preset,
        }));
    };

    const handleSave = () => {
        onSave?.(formData);
    };

    const getFunctionColor = (name) => {
        const hash = [...name].reduce(
            (acc, char) => acc + char.charCodeAt(0),
            0
        );
        return functionColorMap[hash % functionColorMap.length];
    };

    const handleReset = () => {
        showConfirm(intl.formatMessage({ id: "gui.agentManage.confirmResetForm", defaultMessage: "Are you sure you want to reset the form?" })).then((resp) => {
            if (resp) {
                setFormData({
                    agentName: "",
                    selectedPreset: "",
                    roleDescription: "",
                    memo: "",
                    vadConfig: "",
                    asrConfig: "",
                    llmConfig: "",
                    vllmConfig: "",
                    intentConfig: "",
                    memoryConfig: "",
                    ttsConfig: "",
                    voiceColor: "",
                });
            }
        });
    };

    const fetchVoiceOptions = (modelId) => {
        if (!modelId) {
            setVoiceOptions([]);
            return;
        }
        Api.model.getModelVoices(modelId, "", ({ data }) => {
            if (data.code === 0 && data.data) {
                const newData = data.data.map((voice) => ({
                    value: voice.id,
                    label: voice.name,
                }));
                setVoiceOptions(newData);
                // setFormData((prevState) => {
                //     return {
                //         ...prevState,
                //         ttsVoiceId: newData[0].value,
                //     };
                // });
            } else {
                setVoiceOptions([]);
            }
        });
    };

    const filteredHiddenOption = (options) => {
        options = options || [];
        return options.filter((item) => !item.isHidden);
    };

    const handleEditFun = () => {
        setDrawerOpen(true);
    };

    const saveConfig = () => {
        const configData = {
            agentCode: formData.agentCode,
            agentName: formData.agentName,
            asrModelId: formData.model.asrModelId,
            vadModelId: formData.model.vadModelId,
            llmModelId: formData.model.llmModelId,
            vllmModelId: formData.model.vllmModelId,
            ttsModelId: formData.model.ttsModelId,
            ttsVoiceId: formData.ttsVoiceId,
            chatHistoryConf: formData.chatHistoryConf,
            memModelId: formData.model.memModelId,
            intentModelId: formData.model.intentModelId,
            systemPrompt: formData.systemPrompt,
            summaryMemory: formData.summaryMemory,
            langCode: formData.langCode,
            language: formData.language,
            sort: formData.sort,
            functions: currentFunctions.map((item) => {
                return {
                    pluginId: item.id,
                    paramInfo: item.params,
                };
            }),
        };
        showLoading(intl.formatMessage({ id: "gui.agentManage.saving", defaultMessage: "Saving..." }));
        Api.agent.updateAgentConfig(agentId, configData, ({ data }) => {
            hideLoading();
            if (data.code === 0) {
                showSuccess(intl.formatMessage({ id: "gui.agentManage.configSaveSuccess", defaultMessage: "Configuration saved successfully" }));
                onSave();
            } else {
                showDanger(data.msg || intl.formatMessage({ id: "gui.agentManage.configSaveFailed", defaultMessage: "Failed to save configuration" }));
            }
        });
    };

    const resetConfig = async () => {
        const resp = await showConfirm(intl.formatMessage({ id: "gui.agentManage.confirmReset", defaultMessage: "Are you sure you want to reset the configuration?" }));
        if (resp) {
            setFormData({
                agentCode: "",
                agentName: "",
                ttsVoiceId: "",
                chatHistoryConf: 0,
                systemPrompt: "",
                summaryMemory: "",
                langCode: "",
                language: "",
                sort: "",
                model: {
                    ttsModelId: "",
                    vadModelId: "",
                    asrModelId: "",
                    llmModelId: "",
                    vllmModelId: "",
                    memModelId: "",
                    intentModelId: "",
                },
            });
            setCurrentFunctions([]);
            showSuccess(intl.formatMessage({ id: "gui.agentManage.configReset", defaultMessage: "Configuration reset" }));
        }
    };

    const handleSaveFunctions = (data) => {
        setCurrentFunctions(data.updatedFunction);
        setDrawerOpen(false);
    };

    return (
        <div className={styles.container}>
            {/* Config Section Header */}
            <div className={styles.configHeader}>
                <UserOutlined className={styles.configIcon} />
                <span className={styles.configTitle}>
                    <FormattedMessage id="gui.agentManage.roleConfig" defaultMessage="Role Configuration" />
                </span>
                <div className={styles.configRight}>
                    <Alert
                        message={intl.formatMessage({ id: "gui.agentManage.restartNotice", defaultMessage: "After saving the configuration, you need to restart the device for the new configuration to take effect." })}
                        type="info"
                        showIcon
                        className={styles.alert}
                    />
                    <Button
                        style={{ height: 40 }}
                        type="primary"
                        className={styles.saveBtn}
                        onClick={saveConfig}
                    >
                        <FormattedMessage id="gui.agentManage.saveConfig" defaultMessage="Save Configuration" />
                    </Button>
                    <Button
                        style={{ height: 40 }}
                        className={styles.resetBtn}
                        onClick={resetConfig}
                    >
                        <FormattedMessage id="gui.agentManage.reset" defaultMessage="Reset" />
                    </Button>
                </div>
            </div>

            {/* Form Content */}
            <div className={styles.content}>
                <div className={styles.leftColumn}>
                    {/* Assistant Name */}
                    <div className={styles.formItem}>
                        <label className={styles.label}>
                            <FormattedMessage id="gui.agentManage.roleNickname" defaultMessage="Role Nickname:" />
                        </label>
                        <Input
                            value={formData.agentName}
                            onChange={(e) =>
                                handleInputChange("agentName", e.target.value)
                            }
                            className={styles.input}
                        />
                    </div>

                    {/* Role Preset */}
                    <div className={styles.formItem}>
                        <label className={styles.label}>
                            <FormattedMessage id="gui.agentManage.roleTemplate" defaultMessage="Role Template:" />
                        </label>
                        <div className={styles.presetTags}>
                            {templates.map((preset) => (
                                <Tag
                                    key={preset.id}
                                    className={styles.presetTag}
                                    onClick={() => applyTemplateData(preset)}
                                >
                                    {preset.agentName}
                                </Tag>
                            ))}
                        </div>
                    </div>

                    {/* Role Description */}
                    <div className={styles.formItem}>
                        <label className={styles.label}>
                            <FormattedMessage id="gui.agentManage.roleDescription" defaultMessage="Role Description:" />
                        </label>
                        <TextArea
                            value={formData.systemPrompt}
                            onChange={(e) =>
                                handleInputChange(
                                    "systemPrompt",
                                    e.target.value
                                )
                            }
                            rows={9}
                            showCount
                            maxLength={2000}
                            className={styles.textarea}
                        />
                    </div>

                    {/* Memo */}
                    <div className={styles.formItem}>
                        <label className={styles.label}>
                            <FormattedMessage id="gui.agentManage.shortTermMemory" defaultMessage="Dialogue Short-term Memory:" />
                        </label>
                        <TextArea
                            disabled={
                                formData?.model?.memModelId !==
                                "Memory_mem_local_short"
                            }
                            value={formData.summaryMemory}
                            showCount
                            maxLength={2000}
                            onChange={(e) =>
                                handleInputChange(
                                    "summaryMemory",
                                    e.target.value
                                )
                            }
                            rows={6}
                            className={styles.textarea}
                        />
                    </div>
                </div>

                <div className={styles.rightColumn}>
                    {/* ASR Config */}
                    <div className={styles.formItem}>
                        <label className={styles.label}>
                            <FormattedMessage id="gui.agentManage.asr" defaultMessage="Automatic Speech Recognition(ASR):" />
                        </label>
                        <Select
                            value={formData?.model?.asrModelId || ""}
                            onChange={(value) => {
                                setFormData({
                                    ...formData,
                                    model: {
                                        ...formData.model,
                                        asrModelId: value,
                                    },
                                });
                            }}
                            style={{ flex: 1 }}
                            className={styles.input} // Reusing the input style for consistency
                            options={modelOptions?.ASR || []}
                        />
                    </div>

                    {/* LLM Config */}
                    <div className={styles.formItem}>
                        <label className={styles.label}>
                            <FormattedMessage id="gui.agentManage.llm" defaultMessage="Large Language Model(LLM):" />
                        </label>
                        <Select
                            value={formData?.model?.llmModelId || ""}
                            onChange={(value) => {
                                setFormData({
                                    ...formData,
                                    model: {
                                        ...formData.model,
                                        llmModelId: value,
                                    },
                                });
                                curLLMIdRef.current = value;
                                updateIntentOptionsVisibility();
                            }}
                            style={{ flex: 1 }}
                            className={styles.input} // Reusing the input style for consistency
                            options={filteredHiddenOption(modelOptions?.LLM)}
                        />
                    </div>

                    {/* Memory Config */}
                    <div className={styles.formItem}>
                        <label className={styles.label}>
                            <FormattedMessage id="gui.agentManage.memory" defaultMessage="Memory(Memory):" />
                        </label>
                        <div style={{ flex: 1, width: "100%" }}>
                            <div
                                className="odd_line"
                                style={{ flex: 1, width: "100%" }}
                            >
                                <Select
                                    value={formData?.model?.memModelId || ""}
                                    onChange={(value) => {
                                        setFormData((prevState) => {
                                            if (value === "Memory_nomem") {
                                                return {
                                                    ...prevState,
                                                    chatHistoryConf: 0,
                                                    model: {
                                                        ...prevState.model,
                                                        memModelId: value,
                                                    },
                                                };
                                            }

                                            return {
                                                ...prevState,
                                                model: {
                                                    ...prevState.model,
                                                    memModelId: value,
                                                },
                                            };
                                        });
                                    }}
                                    style={{ flex: 1, width: "300px" }}
                                    className={styles.input} // Reusing the input style for consistency
                                    options={filteredHiddenOption(
                                        modelOptions?.Memory
                                    )}
                                />
                                {formData?.model?.memModelId !==
                                    "Memory_nomem" && (
                                    <Radio.Group
                                        style={{ marginLeft: 12 }}
                                        defaultValue={1}
                                        value={formData?.chatHistoryConf || ""}
                                        buttonStyle="solid"
                                        onChange={(e) => {
                                            setFormData((prevState) => {
                                                return {
                                                    ...prevState,
                                                    chatHistoryConf:
                                                        e.target.value,
                                                };
                                            });
                                        }}
                                    >
                                        <Radio.Button value={1}>
                                            <FormattedMessage id="gui.agentManage.reportText" defaultMessage="Report Text" />
                                        </Radio.Button>
                                        <Radio.Button value={2}>
                                            <FormattedMessage id="gui.agentManage.reportTextVoice" defaultMessage="Report Text+Voice" />
                                        </Radio.Button>
                                    </Radio.Group>
                                )}
                            </div>
                            <p
                                style={{
                                    marginTop: "8px",
                                    fontSize: "13px",
                                    color: "#F56C6C",
                                }}
                            >
                                <FormattedMessage id="gui.agentManage.memoryRecommend" defaultMessage="Memory is recommended to use 'Local Short-term Memory' or 'Mem0Ai Memory', it is recommended to select 'Report Text+Voice'" />
                            </p>
                        </div>
                    </div>

                    {/* TTS Config */}
                    <div className={styles.formItem}>
                        <label className={styles.label}>
                            <FormattedMessage id="gui.agentManage.tts" defaultMessage="Text to Speech(TTS):" />
                        </label>
                        <div
                            className="odd_line"
                            style={{ flex: 1, width: "100%" }}
                        >
                            <Select
                                value={formData?.model?.ttsModelId || ""}
                                onChange={(value) => {
                                    setFormData({
                                        ...formData,
                                        ttsVoiceId: "",
                                        model: {
                                            ...formData.model,
                                            ttsModelId: value,
                                        },
                                    });
                                }}
                                style={{ flex: 1, width: "100%" }}
                                className={styles.input} // Reusing the input style for consistency
                                options={filteredHiddenOption(
                                    modelOptions?.TTS
                                )}
                            />
                            <p
                                style={{
                                    marginTop: "8px",
                                    fontSize: "13px",
                                    color: "#F56C6C",
                                }}
                            >
                                <FormattedMessage id="gui.agentManage.ttsDescription" defaultMessage="Text-to-speech refers to the ability to choose different platforms that support text-to-speech functions" />
                            </p>
                        </div>
                    </div>

                    {/* Voice Color */}
                    <div className={styles.formItem}>
                        <label className={styles.label}>
                            <FormattedMessage id="gui.agentManage.roleVoice" defaultMessage="Role Voice:" />
                        </label>
                        <div
                            className="odd_line"
                            style={{ flex: 1, width: "100%" }}
                        >
                            <Select
                                value={formData?.ttsVoiceId || ""}
                                onChange={(value) => {
                                    setFormData({
                                        ...formData,
                                        ttsVoiceId: value,
                                    });
                                }}
                                style={{ flex: 1, width: "100%" }}
                                className={styles.input} // Reusing the input style for consistency
                                options={voiceOptions}
                            />
                            <p
                                style={{
                                    marginTop: "8px",
                                    fontSize: "13px",
                                    color: "#F56C6C",
                                }}
                            >
                                <FormattedMessage id="gui.agentManage.voiceDescription" defaultMessage="Role voice refers to the specific speaker voice that different platforms can support, including male voice, female voice, child voice, etc." />
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            {allFunctionsRef.current && (
                <FunctionManagementDrawer
                    functions={currentFunctions}
                    allFunctions={allFunctionsRef.current}
                    agentId={agentId}
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    onSave={handleSaveFunctions}
                />
            )}
        </div>
    );
};

export default injectIntl(AIConfigContent);